import { Icon } from '@iconify/react'
import {
  Accordion,
  AccordionItem,
  BreadcrumbItem,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Switch,
  Input,
  Slider,
  DatePicker,
  Button,
  Pagination,
  Checkbox,
  Modal,
  useDisclosure,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalContent,
  Textarea,
  CircularProgress,
  Chip,
  Spinner,
} from '@nextui-org/react'
import dayjs from 'dayjs'
import RSelect from 'react-select'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceContextData } from 'src/context/workspace'
import api from 'src/lib/api'
import CountryPicker from './CountryPicker'
import countryObject from 'src/data/country_object.json'
import { CalendarDate } from '@internationalized/date'
import { useSocketContextData } from 'src/context/socket'
import { PowerDocsEventsName } from 'src/types/SocketEvents'
import toast from 'react-hot-toast'
import { useSubscriptionContext } from 'src/context/subscription'
import { SubscribeToEvaluate } from 'src/components/modals/subscribe-to-evaluate'
import { emitter } from 'src/lib/EventEmitter'
import SourcePicker from './SourcePicker'
export default function PublicTenders({ isPrivate, isAdmin }: { isPrivate: boolean; isAdmin: boolean }) {
  const { activeWorkspace } = useWorkspaceContextData()
  const navigate = useNavigate()
  const [semanticSearchEnabled, setSemanticSearchEnabled] = useState<boolean>(false)
  const [tenders, setTenders] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [favorite, setFavorite] = useState<string[]>([])
  const { profile } = useWorkspaceContextData()
  const [users, setUsers] = useState<{ id: number; firstname: string; lastname: string; email: string }[]>([])
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const { isOpen: openedAssignToClient, onOpenChange: onOpenAssignToClientChange } = useDisclosure()
  const loadTenders = useCallback(
    async (workspaceUUID: string, filters: any, page: number) => {
      setLoading(true)
      const params = { ...filters, page, semanticSearchEnabled }
      if (selectedClient && isAdmin) {
        params.forClient = selectedClient
      }
      const res = await api.get(
        isAdmin ? '/admin/tenders' : `${isPrivate ? '/tenders/my-tenders/' : '/tenders/match/'}${workspaceUUID}`,
        {
          params,
        },
      )
      if (res.data?.data) {
        setTenders(
          isPrivate ? res.data.data.map((item: any) => ({ ...item.tender, score: item.score })) : res.data.data,
        )
        setTotalCount(res.data?.totalCount ?? 1)
      }
      setLoading(false)
    },
    [isPrivate, semanticSearchEnabled, selectedClient],
  )
  const getUsers = async () => {
    const res = await api.get('/admin/users')
    setUsers(res.data ?? [])
  }
  useEffect(() => {
    if (isAdmin && profile?.isAdmin) {
      getUsers()
    }
  }, [isAdmin, profile])
  const [savingTender, setSavingTender] = useState<boolean>(false)
  const [scoreTender, setScoreTender] = useState<number>(0)
  const { isOpen: isRejectModalOpen, onOpenChange: onRejectModalChange, onClose: onRejectModalClose } = useDisclosure()
  const [rejecting, setRejecting] = useState<boolean>(false)
  const [reason, setReason] = useState<string>('')
  const { isOpen: isEditValueOpen, onOpenChange: onEditValueOpenChange, onClose: onEditValueClose } = useDisclosure()
  const [tenderToEdit, setTenderToEdit] = useState<any>(null)
  const [valuation, setValuation] = useState<number>(0)
  const [valuationCurrency, setValuationCurrency] = useState<string>('€')

  const updateTenderValue = async () => {
    await toast.promise(
      io.emitWithAck(PowerDocsEventsName.ADMIN_SET_VALUE, {
        token: localStorage.getItem('accessToken'),
        tenderUUID: tenderToEdit?.uuid,
        totalValue: valuation,
        valueCurrency: valuationCurrency,
      }),
      {
        loading: 'updating tender value',
        error: 'Error updating the tenders value',
        success: 'Tender value updated',
      },
    )
    setTenders((prev) =>
      prev.map((tender) => {
        if (tender.uuid === tenderToEdit.uuid) {
          return {
            ...tender,
            totalValue: valuation,
            valueCurrency: valuationCurrency,
          }
        }
        return tender
      }),
    )
    onEditValueClose()
  }
  const rejectTenders = async () => {
    setRejecting(true)
    await io.emitWithAck(PowerDocsEventsName.DISMISS_TENDERS, {
      token: localStorage.getItem('accessToken'),
      tenders: favorite,
      reason,
    })
    setRejecting(false)
    setTenders((prev) => prev.filter((item) => favorite.indexOf(item.uuid) === -1))
    onRejectModalClose()
  }
  const assignTenderToClient = async () => {
    setSavingTender(true)
    await io.emitWithAck(PowerDocsEventsName.ADD_TENDER_TO_USER, {
      token: localStorage.getItem('accessToken'),
      tenders: favorite,
      score: scoreTender,
      clientId: selectedClient,
    })
    setSavingTender(false)
    onOpenAssignToClientChange()
    toast.success('Tenders have been assigned')
  }
  const [page, setPage] = useState<number>(1)
  const [filters, setFilters] = useState<any>({ aiMatch: true })
  const activeFilterCount = useMemo(() => Object.values(filters).filter((item) => item).length, [filters])
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)
  const selectTender = (tenderUUID: string) => {
    setFavorite((prev: string[]) => {
      const oldIndex = prev.indexOf(tenderUUID)
      const copyTable = [...prev]
      if (oldIndex > -1) {
        copyTable.splice(oldIndex, 1)
      } else {
        copyTable.push(tenderUUID)
      }
      return copyTable
    })
  }
  useEffect(() => {
    const fitlersText = localStorage.getItem('filters')
    if (fitlersText) {
      try {
        const filterObj = JSON.parse(fitlersText)
        setFilters(filterObj)
        localStorage.removeItem('filters')
      } catch (err) {}
    }
  }, [])
  useEffect(() => {
    if (activeWorkspace) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
      setPage(1)
      debounceTimeout.current = setTimeout(() => {
        loadTenders(activeWorkspace, filters, 1)
      }, 500)
    }
  }, [filters, activeWorkspace, loadTenders, selectedClient])
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const { io } = useSocketContextData()
  const { getUserSubscription, currentLimitations } = useSubscriptionContext()
  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: openSubscriptionModal,
    onClose: closeSubscriptionModal,
  } = useDisclosure()

  const {loadMyClient} = useWorkspaceContextData()
  const convertToPowerdocs = async () => {
    if (currentLimitations) {
      if (
        currentLimitations.isFreeTrial ||
        (currentLimitations?.hasSubscription && currentLimitations.currentSubscription === 'EVALUATE') ||
        currentLimitations.currentSubscription === 'EXECUTE'
      ) {
        if (
          currentLimitations.currentSubscription === 'EXECUTE' ||
          currentLimitations.currentSubscription === 'EVALUATE'
        ) {
          setIsGenerating(true)
          const toastId = toast.loading('Adding tenders to pipeline...')
          const res = await io.emitWithAck(PowerDocsEventsName.ADD_TENDER_TO_PIPELINE, {
            token: localStorage.getItem('accessToken'),
            workspaceUUID: activeWorkspace,
            tenders: favorite,
            score: favorite?.map(uuid => tenders?.filter(item => item.uuid === uuid)?.pop()?.score),
          })
          if (res.message) {
            toast.dismiss(toastId)
            toast.success(res.message)
          } else {
            toast.dismiss(toastId)
            toast.success('Tenders has been added to pipeline.')
            loadMyClient?.(activeWorkspace)
            emitter.emit('updateSubmissionCount', { step: 1, uuid: activeWorkspace })
            setIsGenerating(false)
            if (res.length === 1) {
              navigate('/client/' + 'powerdocs' + '/project/' + res?.[0]?.uuid + '/documents')
            } else {
              navigate('/pipeline')
            }
          }
        } else {
          openSubscriptionModal()
        }
      } else {
        openSubscriptionModal()
      }
    }
  }
  const formatValue = (tender: any) => {
    try {
      if (!tender.totalValue) return '-'
      return Intl.NumberFormat('de-DE', {
        currency: tender.valueCurrency,
        style: 'currency',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(tender.totalValue)
    } catch (err) {
      return `${Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(tender.valueCurrency)} ${tender.valueCurrency}`
    }
  }
  return (
    <div className="flex flex-col w-full h-screen p-3">
      <Breadcrumbs className={'pl-2 pt-4'}>
        <BreadcrumbItem onClick={() => navigate('/')}>
          <Icon icon={'lucide:home'} /> Home
        </BreadcrumbItem>
        <BreadcrumbItem onClick={() => navigate(`/tenders`)}>Public Tenders</BreadcrumbItem>
      </Breadcrumbs>
      <div className="flex w-full pt-8 pb-6 px-2 2xl:px-8 gap-4 flex-1">
        <div className="flex flex-col w-1/4 border-1 border-default-300 rounded-xl h-full p-4">
          <h2 className="text-md font-medium text-default-900 ">Refine Search</h2>
          <p className="text-xs font-light text-default-400 pb-2">
            Your selection{' '}
            {activeFilterCount > 0 ? (
              <span className="text-xs font-medium text-secondary ">({activeFilterCount} active filters)</span>
            ) : (
              <></>
            )}{' '}
            :
          </p>
          <div className="flex flex-row flex-wrap gap-1 mb-2">
            {Object.keys(filters).map(
              (key) =>
                filters[key] && (
                  <div
                    key={`filter_${key}`}
                    className="border-1 rounded px-2 border-black flex flex-row items-center py-1 gap-2"
                  >
                    <span className="text-xs font-light text-black">
                      {key === 'aiMatch'
                        ? 'AI Match'
                        : key === 'deadline'
                        ? `Deadline: ${dayjs(filters[key].toDate()).format('DD-MM-YYYY')}`
                        : key === 'issueDate'
                        ? `Issue Date: ${dayjs(filters[key].toDate()).format('DD-MM-YYYY')}`
                        : key === 'placeOfPerformance'
                        ? `Country: ${countryObject[filters[key]?.[0]]}${filters[key]?.length > 1 ? ` +${filters[key].length}` : ''}`
                        : key === 'source'
                        ? `Platform: ${countryObject[filters[key]?.[0]]}${filters[key]?.length > 1 ? ` +${filters[key].length}` : ''}`
                        : key === 'country'
                        ? `Buyer Country: ${countryObject[filters[key]]}`
                        : filters[key]}
                    </span>
                    <Icon
                      icon={'lucide:circle-x'}
                      className="cursor-pointer"
                      onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                    />
                  </div>
                ),
            )}
          </div>
          <div className="flex flex-row rounded-xl border-1 border-default-400 py-4 px-4 align-start mt-2 mb-2">
            <div>
              <p className="text-black text-sm font-bold">AI Company Match</p>
              <span className="text-xs text-default-500 font-regular">Match tenders with your company profile</span>
            </div>
            <Switch
              isSelected={!!filters.aiMatch}
              aria-label="aiMatch"
              color="secondary"
              onChange={(e) => setFilters((prev: any) => ({ ...prev, aiMatch: e.target.checked }))}
            />
          </div>
          <div className="flex flex-1">
            <Accordion variant="splitted" className="px-0" defaultExpandedKeys={['by-cpv']}>
              <AccordionItem
                key={'by-cpv'}
                aria-label={'cpv'}
                title={'By CPV'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.cpv ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.cpv ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <div>
                  <Input
                    placeholder="e.g 48000000"
                    onChange={(e) => setFilters((prev: any) => ({ ...prev, cpv: e.target.value }))}
                    variant="bordered"
                    label="CPV Code"
                    maxLength={8}
                    minLength={8}
                    errorMessage={'CPV code must have 8 digits'}
                    value={filters.cpv ?? ''}
                  />
                </div>
              </AccordionItem>
              <AccordionItem
                key={'by-place'}
                aria-label={'place-of-performace'}
                title={'Country'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.placeOfPerformance ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.placeOfPerformance ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <CountryPicker
                  selectedValue={filters.placeOfPerformance}
                  onSelect={(value: string) => setFilters((prev: any) => ({ ...prev, placeOfPerformance: value }))}
                />
              </AccordionItem>
              <AccordionItem
                key={'by-source'}
                aria-label={'source'}
                title={'Platform'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.source ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.source ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <SourcePicker
                  selectedValue={filters.source}
                  onSelect={(value: string) => setFilters((prev: any) => ({ ...prev, source: value }))}
                />
              </AccordionItem>
              <AccordionItem
                key={'by-value'}
                aria-label={'estimated-value'}
                title={'Estimate contract value'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.minValue ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.minValue ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <div>
                  <Slider
                    className="max-w-md"
                    defaultValue={0}
                    label="min. value"
                    maxValue={100000000}
                    minValue={0}
                    step={10000}
                    value={filters.minValue}
                    onChange={(value) => setFilters((prev: any) => ({ ...prev, minValue: value }))}
                    getValue={(v) => `${v} €`}
                  />
                </div>
              </AccordionItem>
              <AccordionItem
                key={'by-issuance-date'}
                aria-label={'issuance-date'}
                title={'By issue date'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.issueDate ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.issueDate ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <div>
                  <DatePicker
                    className="w-full"
                    label="Issue date"
                    variant="bordered"
                    value={filters.issueDate ?? undefined}
                    onChange={(value) => {
                      console.log(value)
                      setFilters((prev: any) => ({
                        ...prev,
                        issueDate: value ? new CalendarDate(value.year, value.month, value.day) : undefined,
                      }))
                    }}
                  />
                </div>
              </AccordionItem>
              <AccordionItem
                key={'by-deadline-date'}
                aria-label={'deadline-date'}
                title={'By Minimum Deadline Date'}
                classNames={{
                  trigger: `px-2 rounded-xl ${
                    filters.deadline ? 'bg-secondary text-white' : 'bg-[#F5F5F5]'
                  } flex-row-reverse`,
                  title: `text-xs ${filters.deadline ? 'text-white' : ''}`,
                  base: 'px-0 shadow-none',
                }}
                indicator={<Icon icon={'lucide:chevron-right'} />}
              >
                <div>
                  <DatePicker
                    className="w-full"
                    label="Deadline Date"
                    variant="bordered"
                    value={filters.deadline ?? undefined}
                    onChange={(value) => {
                      console.log(value)
                      setFilters((prev: any) => ({
                        ...prev,
                        deadline: value ? new CalendarDate(value.year, value.month, value.day) : undefined,
                      }))
                    }}
                  />
                </div>
              </AccordionItem>
            </Accordion>
          </div>
          <div>
            <Button className="w-full" variant="bordered" color="secondary" onPress={() => setFilters({})}>
              Clear all filters
            </Button>
          </div>
        </div>
        <div className="flex flex-col flex-1 gap-4">
          <h1 className="text-xl font-bold text-default-900 lg:text-3xl">All Public Tenders</h1>
          <div className="flex flex-row justify-between">
            <div>
              <Input
                className="min-w-[350px]"
                classNames={{
                  inputWrapper: 'pr-1',
                }}
                placeholder="e.g Construction"
                onChange={(e) => setFilters((prev: any) => ({ ...prev, title: e.target.value }))}
                variant="bordered"
                label="Search"
                value={filters.title ?? ''}
                size="sm"
                endContent={
                  <Button
                    onPress={() => setSemanticSearchEnabled((prev: boolean) => !prev)}
                    className="items-center self-center"
                    variant={semanticSearchEnabled ? 'solid' : 'ghost'}
                    color={semanticSearchEnabled ? 'secondary' : 'default'}
                  >
                    <Icon icon="lucide:brain-circuit" fontSize={24} />
                  </Button>
                }
              />
            </div>
            <div className="flex flex-row gap-2">
              {!isAdmin && (
                <Button
                  isDisabled={favorite.length === 0}
                  color="danger"
                  onPress={onRejectModalChange}
                  startContent={<Icon icon="lucide:trash" />}
                >
                  Not interested {favorite.length > 0 ? ` (x${favorite.length} tenders)` : ''}
                </Button>
              )}
              {isAdmin && profile?.isAdmin && (
                <RSelect
                  className={'flex flex-1 w-full'}
                  onChange={(option) => setSelectedClient(option?.value)}
                  placeholder={`Sort Tenders for Specific User`}
                  options={users
                    .filter((item) => item.id !== profile.uuid)
                    .map((client) => ({
                      value: client.id,
                      label: [client.firstname ?? '', client.lastname ?? '', ' - ', client.email].join(' ').trim(),
                    }))}
                  styles={{
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#3D969E' : '#fff',
                      color: state.isFocused ? '#fff' : '#333',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: '#333',
                      fontWeight: '500',
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      padding: '0',
                    }),
                    control: (base, state) => ({
                      ...base,
                      width: '100%', // Ensure the control spans the full width
                      minWidth: '100%',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      border: state.isFocused ? '1px solid #000' : '1px solid #E0E0E0',
                      boxShadow: state.isFocused ? '0 0 4px rgba(0, 114, 245, 0.3)' : 'none',
                      padding: '4px',
                      transition: 'border 0.2s ease, box-shadow 0.2s ease',
                    }),
                    menu: (base) => ({
                      ...base,
                      width: '100%', // Dropdown menu spans full width
                      minWidth: '100%',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                  }}
                  menuPortalTarget={window.document.querySelector('body')}
                />
              )}
              {isAdmin && profile?.isAdmin ? (
                <Button
                  isDisabled={favorite.length === 0}
                  color="secondary"
                  onClick={onOpenAssignToClientChange}
                  startContent={<Icon icon="lucide:circle-plus" />}
                >
                  Asign tenders to User {favorite.length > 0 ? ` (x${favorite.length} tenders)` : ''}
                </Button>
              ) : (
                <Button
                  isDisabled={favorite.length === 0}
                  color="secondary"
                  onClick={convertToPowerdocs}
                  startContent={<Icon icon="lucide:circle-plus" />}
                >
                  Add to pipeline{favorite.length > 0 ? ` (x${favorite.length} tenders)` : ''}
                </Button>
              )}
            </div>
          </div>
          <div className="relative">
            <Table
              classNames={{
                wrapper: 'p-0 shadow-none',
              }}
              aria-label="Table of public tenders"
            >
              <TableHeader>
                <TableColumn>Project description</TableColumn>
                <TableColumn>AI Score</TableColumn>
                <TableColumn>Total Value</TableColumn>
                <TableColumn>Platform</TableColumn>
                <TableColumn>Country</TableColumn>
                <TableColumn>Creation date</TableColumn>
                <TableColumn>Due date</TableColumn>
              </TableHeader>
              <TableBody>
                {tenders.map((tender) => (
                  <TableRow key={tender.uuid}>
                    <TableCell className="max-w-[350px]">
                      <div className="flex flex-row">
                        <Checkbox
                          checked={favorite.indexOf(tender.uuid) > -1}
                          onChange={() => selectTender(tender.uuid)}
                        />
                        <div className="flex flex-col">
                          <a onClick={() => window.open(tender.url)} className="text-xs font-bold cursor-pointer">
                            {tender.publicationNumber?.substring(0, 12)}
                          </a>
                          <div
                            className="flex flex-col"
                            onClick={(e) => {
                              console.log(e)
                              localStorage.setItem('filters', JSON.stringify(filters))
                              // Check if Ctrl or Cmd key is pressed
                              if (e.ctrlKey || e.metaKey) {
                                // Open in new tab
                                window.open(
                                  `/${isAdmin && profile?.isAdmin ? 'admin/tender' : 'my-tenders'}/${
                                    tender.uuid
                                  }/overview`,
                                  '_blank',
                                )
                              } else {
                                // Navigate in current tab
                                navigate(
                                  `/${isAdmin && profile?.isAdmin ? 'admin/tender' : 'my-tenders'}/${
                                    tender.uuid
                                  }/overview`,
                                )
                              }
                            }}
                          >
                            <span className="text-black text-xs font-bold cursor-pointer hover:underline">
                              {tender.title}
                            </span>
                            <span className="text-default-400 text-xs font-medium">Buyer {tender.buyer}</span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <CircularProgress
                        color={tender?.score >= 70 ? 'secondary' : tender?.score >= 40 ? 'warning' : 'success'}
                        showValueLabel={true}
                        size="lg"
                        value={tender?.score ?? 10}
                      />
                    </TableCell>
                    <TableCell
                      onClick={() => {
                        if (isAdmin && profile?.isAdmin) {
                          setTenderToEdit(tender)
                          onEditValueOpenChange()
                        }
                      }}
                      className={isAdmin && profile?.isAdmin ? 'cursor-pointer' : ''}
                    >
                      {formatValue(tender)}
                    </TableCell>
                    <TableCell>
                      <Chip color="secondary" variant="flat">
                        {tender.source}
                      </Chip>
                    </TableCell>
                    <TableCell>{countryObject?.[tender.country?.toUpperCase()] || tender.country || 'N/A'}</TableCell>
                    <TableCell>{dayjs(tender.publicationDate).format('YYYY-MM-DD')}</TableCell>
                    <TableCell>{tender.deadline ? dayjs(tender.deadline).format('YYYY-MM-DD') : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && tenders.length === 0 && (
              <div className="flex justify-center items-center flex-col gap-4 pt-40 pb-40 flex-1 absolute bg-white z-10 w-full h-full">
                <Spinner size={'lg'} color={'secondary'} />
                <span className="text-default-400 text-italic font-light text-2xl">Loading tenders...</span>
              </div>
            )}
            <div className="flex flex-row z-1 justify-between">
              <div>
                {loading && tenders.length > 0 && (
                  <div className="flex items-center gap-4 pt-2">
                    <Spinner size={'sm'} />
                    <span className="text-sm italic">loading page</span>
                  </div>
                )}
              </div>
              <Pagination
                initialPage={1}
                color="secondary"
                size={'sm'}
                total={Math.floor(totalCount / 8) - 1}
                onChange={(pageNumber: number) => {
                  setPage(pageNumber)
                  loadTenders(activeWorkspace, filters, pageNumber)
                }}
                classNames={{
                  base: 'z-0',
                }}
                page={page}
              />
            </div>
          </div>
        </div>
      </div>
      <SubscribeToEvaluate isOpen={isSubscriptionModalOpen} onClose={closeSubscriptionModal} />
      <Modal
        isOpen={isRejectModalOpen}
        onOpenChange={onRejectModalChange}
        backdrop="blur"
        size="xl"
        className="z-[1000] select-dialog"
        isDismissable={false}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Dismiss Tenders</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <Textarea
                    isRequired={false}
                    labelPlacement="outside"
                    placeholder="e.g The valuation of the project doesn't meet our minimum threshold"
                    label="Why you think these tinders are not interesting ?"
                    description="This will help our recommendation engine to better select results that suits your interest and company profile"
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="default" onPress={onClose} variant="bordered">
                  Cancel
                </Button>
                <Button color="danger" onPress={rejectTenders} disabled={rejecting}>
                  Dismiss Tenders
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isEditValueOpen}
        onOpenChange={onEditValueOpenChange}
        backdrop="blur"
        size="xl"
        className="z-[1000] select-dialog"
        isDismissable={false}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Edit Tender Value - #${tenderToEdit?.id}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <Input
                    labelPlacement="outside"
                    placeholder="e.g 12000"
                    label="Value"
                    min={0}
                    type="number"
                    onChange={(e) => setValuation(e.target.value)}
                  />
                  <Input
                    labelPlacement="outside"
                    placeholder="e.g EUR"
                    label="Currency"
                    onChange={(e) => setValuationCurrency(e.target.value)}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="default" onPress={onClose} variant="bordered">
                  Cancel
                </Button>
                <Button color="danger" onPress={updateTenderValue} disabled={rejecting}>
                  Save Value
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={openedAssignToClient}
        onOpenChange={onOpenAssignToClientChange}
        backdrop="blur"
        size="xl"
        className="z-[1000] select-dialog"
        isDismissable={false}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Asign to Client</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <Input
                    labelPlacement="outside"
                    placeholder="e.g 80%"
                    label="Matching Score"
                    min={0}
                    max={100}
                    type="number"
                    defaultValue={
                      favorite?.[0] ? tenders.filter((item) => item?.uuid === favorite?.[0])?.pop()?.score ?? 0 : 0
                    }
                    onChange={(e) => setScoreTender(e.target.value)}
                  />
                  <div className={' relative flex flex-col gap-1'}>
                    <span className={'text-medium ml-1 text-sm'}>Merge with</span>
                    <RSelect
                      className={'flex flex-1 w-full'}
                      onChange={(option) => setSelectedClient(option?.value)}
                      placeholder={`Assign tenders to client`}
                      options={users
                        .filter((item) => item.id !== profile.uuid)
                        .map((client) => ({
                          value: client.id,
                          label: [client.firstname ?? '', client.lastname ?? '', ' - ', client.email].join(' ').trim(),
                        }))}
                      defaultValue={
                        users
                          .filter((item) => item.id === selectedClient)
                          .map((client) => ({
                            value: client.id,
                            label: [client.firstname ?? '', client.lastname ?? '', ' - ', client.email]
                              .join(' ')
                              .trim(),
                          }))?.[0]
                      }
                      styles={{
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? '#3D969E' : '#fff',
                          color: state.isFocused ? '#fff' : '#333',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#333',
                          fontWeight: '500',
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '0',
                        }),
                        control: (base, state) => ({
                          ...base,
                          width: '100%', // Ensure the control spans the full width
                          minWidth: '100%',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          border: state.isFocused ? '1px solid #000' : '1px solid #E0E0E0',
                          boxShadow: state.isFocused ? '0 0 4px rgba(0, 114, 245, 0.3)' : 'none',
                          padding: '4px',
                          transition: 'border 0.2s ease, box-shadow 0.2s ease',
                        }),
                        menu: (base) => ({
                          ...base,
                          width: '100%', // Dropdown menu spans full width
                          minWidth: '100%',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          overflow: 'hidden',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                      }}
                      menuPortalTarget={window.document.querySelector('body')}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onPress={assignTenderToClient} disabled={savingTender}>
                  {savingTender ? 'Saving ...' : 'Assign'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
